import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import AnnouncementComponent from "../components/AnnouncementModal";
import PerformanceStatsModal from "../components/Stats";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

/* ── stat card pill ──────────────────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value ?? 0}</span>
    </div>
);

/* ── stat tile (large number card) ──────────────────────────────────────── */
const StatTile = ({ label, value, colorClass, borderClass, textClass, numClass }) => (
    <div className={`flex flex-col gap-1 px-5 py-4 rounded-xl border ${borderClass} ${colorClass}`}
        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
        <p className={`text-[10px] font-bold uppercase tracking-widest ${textClass}`}>{label}</p>
        <p className={`text-3xl font-extrabold ${numClass}`}>{value ?? 0}</p>
    </div>
);

/* ════════════════════════════════════════════════════════════════════════════ */
const HomePage = ({ onLogout }) => {

    const [sidebarOpen, setSidebarOpen]             = useState(false);
    const [employee, setEmployee]                   = useState(null);
    const [attendanceSummary, setAttendanceSummary] = useState({});
    const [leaveSummary, setLeaveSummary]           = useState({});
    const [holidays, setHolidays]                   = useState([]);
    const [showStatsModal, setShowStatsModal]       = useState(false);

    const [monthYear, setMonthYear] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });

    const currentYear = new Date().getFullYear();

    /* ---------------- LOAD EMPLOYEE ---------------- */
    useEffect(() => {
        const storedEmployee = localStorage.getItem("employee");
        if (storedEmployee) setEmployee(JSON.parse(storedEmployee));
    }, []);

    /* ---------------- ATTENDANCE SUMMARY ---------------- */
    const fetchAttendanceSummary = async () => {
        if (!employee?.employee_id) return;
        try {
            const res = await axios.get(
                `${SERVER_URL}/Rahul/attendanceAndLeaveSummary.php`,
                { params: { employee_id: employee.employee_id, month: monthYear } }
            );
            setAttendanceSummary(res.data.attendance_summary || {});
            setLeaveSummary(res.data.leave_summary || []);
        } catch (err) {
            console.error("Attendance summary error", err);
        }
    };

    /* ---------------- HOLIDAY LIST ---------------- */
    const fetchHolidays = async () => {
        try {
            const res = await axios.get(`${SERVER_URL}/holidays.php`, { params: { year: currentYear } });
            setHolidays(res.data);
        } catch (err) {
            console.error("Holiday list error", err);
        }
    };

    /* ---------------- LOAD DATA ---------------- */
    useEffect(() => {
        if (!employee) return;
        fetchAttendanceSummary();
        fetchHolidays();
    }, [employee, monthYear]);

    /* ════════════════════════════════ JSX ════════════════════════════════ */
    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .att-scrollbar::-webkit-scrollbar-track { background: rgba(226,232,240,0.6); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
            `}</style>

            <div className="flex min-h-screen font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={onLogout} />

                <div className="flex-1 flex flex-col">

                    {/* ── STICKY HEADER ── */}
                    <div className="sticky top-0 z-40 border-b border-slate-200"
                        style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                        <div className="px-7 py-3.5 flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold tracking-tight text-slate-800 m-0">
                                    Home
                                </h1>
                            </div>
                        </div>
                    </div>

                    {/* ── CONTENT ── */}
                    <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">

                        {/* LEFT SIDE */}
                        <div className="lg:col-span-2 flex flex-col gap-5">

                            {/* ── QUICK STAT PILLS (top summary bar) ── */}
                            {/* <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-1 h-5 rounded-full bg-blue-700" />
                                    <span className="text-sm font-bold text-slate-700">
                                        {employee?.employee_name || "Employee"} — Quick Overview
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <StatCard label="Working Days" value={attendanceSummary.working_days} colorClass="bg-blue-50 text-blue-700 border border-blue-200"       dotClass="bg-blue-500" />
                                    <StatCard label="Present"      value={attendanceSummary.present_days} colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200" dotClass="bg-emerald-500" />
                                    <StatCard label="Absent"       value={attendanceSummary.absent_days}  colorClass="bg-rose-50 text-rose-700 border border-rose-200"         dotClass="bg-rose-500" />
                                    <StatCard label="Leave"        value={attendanceSummary.leave_days}   colorClass="bg-amber-50 text-amber-700 border border-amber-200"       dotClass="bg-amber-500" />
                                    <StatCard label="Holidays"     value={attendanceSummary.holidays}     colorClass="bg-sky-50 text-sky-700 border border-sky-200"             dotClass="bg-sky-500" />
                                </div>
                            </div> */}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                {/* ── ATTENDANCE SUMMARY CARD ── */}
                                <div className="rounded-xl p-5 bg-white border border-slate-200"
                                    style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1 h-5 rounded-full bg-blue-700" />
                                            <h2 className="font-bold text-sm text-slate-800 m-0">Attendance Summary</h2>
                                        </div>
                                        <input
                                            type="month"
                                            value={monthYear}
                                            onChange={(e) => setMonthYear(e.target.value)}
                                            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                                        />
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <StatTile
                                            label="Working" value={attendanceSummary.working_days}
                                            colorClass="bg-blue-50"    borderClass="border-blue-200"
                                            textClass="text-blue-600"   numClass="text-blue-800"
                                        />
                                        <StatTile
                                            label="Present" value={attendanceSummary.present_days}
                                            colorClass="bg-emerald-50"  borderClass="border-emerald-200"
                                            textClass="text-emerald-600" numClass="text-emerald-800"
                                        />
                                        <StatTile
                                            label="Absent" value={attendanceSummary.absent_days}
                                            colorClass="bg-rose-50"    borderClass="border-rose-200"
                                            textClass="text-rose-600"   numClass="text-rose-800"
                                        />
                                        <StatTile
                                            label="Leave" value={attendanceSummary.leave_days}
                                            colorClass="bg-amber-50"   borderClass="border-amber-200"
                                            textClass="text-amber-600"  numClass="text-amber-800"
                                        />
                                        <StatTile
                                            label="Holidays" value={attendanceSummary.holidays}
                                            colorClass="bg-sky-50"     borderClass="border-sky-200"
                                            textClass="text-sky-600"    numClass="text-sky-800"
                                        />
                                    </div>
                                </div>

                                {/* ── LEAVE SUMMARY CARD ── */}
                                <div className="rounded-xl p-5 bg-white border border-slate-200"
                                    style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-1 h-5 rounded-full bg-blue-700" />
                                        <h2 className="font-bold text-sm text-slate-800 m-0">Leave Summary</h2>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <StatTile
                                            label="Total CL" value={leaveSummary.total_casual_leave}
                                            colorClass="bg-blue-50"    borderClass="border-blue-200"
                                            textClass="text-blue-600"   numClass="text-blue-800"
                                        />
                                        <StatTile
                                            label="Used CL" value={leaveSummary.used_casual_leave}
                                            colorClass="bg-rose-50"    borderClass="border-rose-200"
                                            textClass="text-rose-600"   numClass="text-rose-800"
                                        />
                                        <StatTile
                                            label="Rem. CL" value={leaveSummary.remaining_casual_leave}
                                            colorClass="bg-emerald-50"  borderClass="border-emerald-200"
                                            textClass="text-emerald-600" numClass="text-emerald-800"
                                        />
                                        <StatTile
                                            label="Total SL" value={leaveSummary.total_sick_leave}
                                            colorClass="bg-blue-50"    borderClass="border-blue-200"
                                            textClass="text-blue-600"   numClass="text-blue-800"
                                        />
                                        <StatTile
                                            label="Used SL" value={leaveSummary.used_sick_leave}
                                            colorClass="bg-rose-50"    borderClass="border-rose-200"
                                            textClass="text-rose-600"   numClass="text-rose-800"
                                        />
                                        <StatTile
                                            label="Rem. SL" value={leaveSummary.remaining_sick_leave}
                                            colorClass="bg-emerald-50"  borderClass="border-emerald-200"
                                            textClass="text-emerald-600" numClass="text-emerald-800"
                                        />
                                    </div>
                                </div>

                            </div>

                            {/* ── ATTENDANCE PROGRESS BAR (if data exists) ── */}
                            {attendanceSummary.working_days > 0 && (
                                <div className="rounded-xl p-5 bg-white border border-slate-200"
                                    style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-1 h-5 rounded-full bg-blue-700" />
                                        <span className="text-sm font-bold text-slate-800">Attendance Rate</span>
                                        <span className="ml-auto text-xs font-bold text-slate-700">
                                            {Math.round((attendanceSummary.present_days / attendanceSummary.working_days) * 100)}%
                                        </span>
                                    </div>
                                    <div className="rounded-xl px-4 py-3 bg-slate-50 border border-slate-200">
                                        <div className="w-full h-2 rounded-full overflow-hidden bg-slate-200">
                                            <div
                                                className="h-full rounded-full transition-all duration-700"
                                                style={{
                                                    width: `${Math.round((attendanceSummary.present_days / attendanceSummary.working_days) * 100)}%`,
                                                    background: "linear-gradient(90deg, #1e40af, #0369a1)",
                                                }}
                                            />
                                        </div>
                                        <div className="mt-2">
                                            <span className="text-[10px] text-slate-500">
                                                {attendanceSummary.present_days} present · {attendanceSummary.leave_days} leave · {attendanceSummary.absent_days} absent
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* ── ANNOUNCEMENTS ── */}
                        <div
                            className="rounded-xl border border-slate-200 bg-white sticky top-[61px] h-[calc(100vh-80px)] overflow-hidden"
                            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
                        >
                            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
                                <div className="w-1 h-5 rounded-full bg-blue-700" />
                                <span className="text-sm font-bold text-slate-800">Announcements</span>
                            </div>
                            <div className="overflow-y-auto h-[calc(100%-57px)] att-scrollbar">
                                {employee && <AnnouncementComponent employee={employee} />}
                            </div>
                        </div>

                        <PerformanceStatsModal
                            isOpen={showStatsModal}
                            onClose={() => setShowStatsModal(false)}
                        />

                    </div>
                </div>
            </div>
        </>
    );
};

export default HomePage;